import style from './ManageStorage.module.css';

import error from 'assets/svg/attention-01.svg';

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Modal from 'ui/Modal';
import Icon from 'ui/Icon';
import FormTextField from 'ui/formItems/FormTextField';
import ColorPicker from '../../../Widgets/ColorPicker';
import BarcodesMultiList from '../../../Widgets/BarcodesMultiList';
import { AutoComplete } from 'components/WorkRight/WorkRightStorageDir/WorkRightStorageAddGroup/autoComplete';
import useAlert from 'hooks/useAlert';
import { editStorage, getStorage, saveAutoCompleteByStorageId } from 'services/storage.services';
import { getListBarcodes, getListIcon, getListTypeStorages } from 'services/list.services';
import { address_server_short, PUBLIC_ASSETS } from 'config';

const ManageStorageModal = ({ id, data = undefined, open, onClose }) => {
  const { showAlert } = useAlert();
  const formContext = useForm();
  const { handleSubmit, setValue, watch, reset } = formContext;

  const [storageTypesSource, setStorageTypesSource] = useState([]);
  const [storageTypes, setStorageTypes] = useState([]);

  const [selectedPackType, setSelectedPackType] = useState(0);
  const [selectedPackCount, setSelectedPackCount] = useState(0);

  const [nameError2, setNameError2] = useState(false);
  const [nameError3, setNameError3] = useState(false);
  const [loading, setLoading] = useState(false);

  const { color, description, location, barcodes, type, icon } = watch();

  useEffect(() => {
    fetchData();

    return () => reset();
  }, []);

  const fetchData = useCallback(async () => {
    const [types, icons] = await Promise.all([getListTypeStorages(), getListIcon()]);
    setStorageTypesSource(types);
    loadItem(icons);
  }, []);

  const loadItem = useCallback(
    async (icons) => {
      if (!id) {
        reset({
          ...data,
          barcodes: data?.barcode ? [{ id: data?.barcode?.type, value: data?.barcode?.value }] : [],
          icon: { id: icons.find((i) => i.file === data.icon)?.id ?? 1, file: data.icon },
        });
        return;
      }
      const storage = await getStorage(id);
      const formattedStorage = {
        ...storage,
        barcodes:
          storage?.barcodes?.map((barcode) => ({
            id: barcode.type,
            value: barcode.value,
          })) || [],
      };

      reset(formattedStorage);
    },
    [id, data, reset],
  );

  const handleSaveAutoComplete = async (result) => {
    const res = await saveAutoCompleteByStorageId(
      result?.id_parent_storage ?? result,
      storageTypes,
      selectedPackCount,
      selectedPackType,
    );
    if (!res.success) {
      showAlert('Ошибка автозаполнения', 'error');
    }
  };

  const onSubmit = async (formData) => {
    setLoading(true);
    const payload = {
      name: formData.name,
      type: formData.type,
      location: formData.type === 1 ? 0 : (location?.parent ?? 0),
      barcodes: formData.barcodes?.filter((b) => b.id && b.value) ?? [],
      packs: formData.packs ?? [{ id: null, type: 0 }],
      icon: formData?.icon?.id,
      color: formData.color,
      copy: formData.copy ?? 0,
      comments: formData.comment,
    };

    const action = async () => {
      const res = await editStorage(id ?? 0, payload);
      if (res.success) {
        showAlert(id ? 'Хранилище обновлено!' : 'Хранилище создано!', 'success');
        if (type === 2) await handleSaveAutoComplete(res.result);
        onClose({ open: false, item: res.result });
      } else {
        if (res.error_code === 2015) setNameError2(true);
        else if (res.error_code === 2029) setNameError3(true);
        else showAlert(res.error_code, 'error');
      }
    };

    if (id) {
      showAlert('Изменить данные хранилища?', 'sure', async () => await action());
    } else {
      await action();
    }

    setLoading(false);
  };

  const nameField = useMemo(
    () => (
      <>
        <FormTextField
          name="name"
          label="Наименование"
          required
          rules={{ required: 'Поле обязательно' }}
        />
        {nameError2 && (
          <div className={style.dataErrorBlock}>
            <img src={error} alt="error" />
            <div className={style.dataError}>Поле должно быть уникальным</div>
          </div>
        )}
      </>
    ),
    [nameError2],
  );

  const barcodeField = useMemo(
    () => (
      <>
        <BarcodesMultiList
          list={barcodes?.map((item) => {
            return { ...item, id: item.id };
          })}
          getItemsList={getListBarcodes}
          setList={(val) => setValue('barcodes', val)}
          multiList={false}
        />
        {nameError3 && (
          <div className={style.dataErrorBlock}>
            <img src={error} alt="error" />
            <div className={style.dataError}>Штрихкод должен быть уникальным</div>
          </div>
        )}
      </>
    ),
    [barcodes, nameError3],
  );

  const iconPreview = useMemo(() => {
    if (!icon?.file) return null;
    return (
      <Icon
        width="40px"
        height="40px"
        src={`${address_server_short}${PUBLIC_ASSETS}${icon.file}`}
      />
    );
  }, [icon]);

  return (
    <FormProvider {...formContext}>
      <Modal
        title="Хранилище"
        open={open}
        onClose={() => onClose({ open: false, item: null })}
        onSubmit={handleSubmit(onSubmit)}
        submitText="Сохранить"
        disabledSubmit={loading}
      >
        <div className={style.wrapper}>
          <div className={style.info}>
            <div className={style.infoItem}>
              <div className={style.infoItemName}>Тип</div>
              <div className={style.value}>
                {storageTypesSource.find((s) => s.id === type)?.name ?? ''}
              </div>
            </div>

            <div className={style.storageAddress}>
              <div className={style.infoItemName}>Расположение</div>
              <div className={style.value}>{location?.path || 'Глобальное хранилище'}</div>
            </div>

            <div className={style.infoItem}>{nameField}</div>

            <div className={style.barcode}>
              <div className={style.infoItemName}>Штрихкод</div>
              {barcodeField}
            </div>

            <div className={style.infoItemIcon}>
              <div className={style.infoItemName}>Иконка</div>
              {iconPreview}
            </div>

            <div className={style.infoItem}>
              <div className={style.infoItemName}>Цвет</div>
              <div>
                <ColorPicker
                  selected={{ color: color ?? '#000000' }}
                  onChange={({ color }) => setValue('color', color.slice(0, -2))}
                />
              </div>
            </div>
          </div>

          <div className={style.description}>
            <div className={style.infoItemNameAbout}>Описание</div>
            <textarea
              placeholder="Описание хранилища"
              value={description ?? ''}
              onChange={(e) => setValue('description', e.target.value)}
              style={{ backgroundColor: 'transparent' }}
            />
          </div>

          {type === 2 && (
            <AutoComplete
              storageTypes={storageTypes}
              setStorageTypes={setStorageTypes}
              selectedPackCount={selectedPackCount}
              setSelectedPackCount={setSelectedPackCount}
              selectedPackType={selectedPackType}
              setSelectedPackType={setSelectedPackType}
            />
          )}
        </div>
      </Modal>
    </FormProvider>
  );
};

export default ManageStorageModal;
